import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { SharedService } from '../../../shared-components/shared.service';
import { Alerts } from '../../../shared-components/student/student.model';
import { UserPreferencesService } from '../../../shared-components/user-preferences/user-preferences.service';
import { UserPermission } from '../../../shared-components/user/user-permission.model';
import { User } from '../../../shared-components/user/user.model';
import { UserService } from '../../../shared-components/user/user.service';
import { SchoolYearSettings } from '../../../shared-components/year/school-year-settings.model';
import { Year } from '../../../shared-components/year/year.model';
import { YearService } from '../../../shared-components/year/year.service';
import { GradeScaleDescription } from '../../../shared-modules/grading-scales/grade-scale-description.model';
import { ClassroomSummary } from '../../classroom-summary/models/classroom-summary.model';
import { GradebookAttachment } from '../shared/models/gradebook-attachment.model';
import { GradebookIndicator } from '../shared/models/gradebook-indicator.model';
import { GradebookPreferences } from '../shared/models/preferences/gradebook-preferences.model';
import { SharedGradebookService } from '../shared/shared-gradebook.service';
import { System } from './../../../shared-components/System';
import { AnalysisStudentsAttendance } from './models/analysis-students-attendance.model';
import { AnalysisTerm } from './models/analysis-term.model';
import { AssignmentToStandard } from './models/assignment-to-standard.model';
import { Assignment } from './models/assignment.model';
import { Category } from './models/category.model';
import { Formula } from './models/formula.model';
import { GradebookBackup } from './models/gradebook-backup.model';
import { GradebookCategoryPointsSummary } from './models/gradebook-category-points-summary.model';
import { GradebookCurriculumStandards } from './models/gradebook-curriculum-standards.model';
import { GradebookMismatchScores } from './models/gradebook-mismatch-scores.model';
import { GradebookSettings } from './models/gradebook-settings.model';
import { GradebookStudentMarkByStudentId } from './models/gradebook-student-mark-by-student-id.model';
import { GradebookStudent } from './models/gradebook-student.model';
import { GradebookSystemGradeAssignment } from './models/gradebook-system-grade-assignment.model';
import { GradebookUndoRedo } from './models/gradebook-undo-redo.model';
import { BehaviorSkillGrades, CurriculumMarksType, GradingPeriodCommentDTO, RCGBehaviourSkill, RCGStandard, ReportCardGrades, TermGradeCodes, TermgradeCycleHeader, TermGrades, ValidGradeTemplate } from './models/report-card-grades-dto.model';
import { ScoresAssignment } from './models/scores-assignment.model';
import { ScoresStudentMark } from './models/scores-student-mark.model';
import { SpecialMark } from './models/special-mark.model';
import { Standard } from './models/standard.model';
import { StandardsStudentMark } from './models/standards-student-mark.model';
import { TermGradesBackup } from './models/term-grades-backup.model';
import { Term } from './models/term.model';
import { Unit } from './models/unit.model';

@Injectable({
    providedIn: 'root'
})
export class GradebookV9Service {

    defaultYear: Year;
    schoolYearSettings: SchoolYearSettings;
    course: ClassroomSummary;
    yearId = 0;
    schoolId = 0;
    siteYearId = 0;
    pageHeight = 0;
    currentUser: User;
    // --------------- Tab Flags -----------------
    selectedTab: string;
    selectedStandardTab: string;
    selectedReportingTab: string;
    selectedReportingStandardTab: string;
    selectedAnalysisTab: string;
    // --------------- Filter Flags -----------------
    selectedTerm: Term;
    selectedCategory: Category;
    selectedUnit: Unit;
    selectedStandardAssignmentId: number;
    selectedStandardStudentId: number;
    selectedStandardId: number;
    unsavedChanges = false;
    showIndicators = false;
    nonScoreStandard = false;
    showAssignment = false;
    showImportStandardModal = false;
    // --------------- Gradebook Objects -----------
    terms: Term[] = [];
    categories: Category[] = [];
    units: Unit[] = [];
    indicators: GradebookIndicator[] = [];
    specialMarks: SpecialMark[] = [];
    gradeScaleDescriptions: GradeScaleDescription[] = [];
    gradebookStudents: GradebookStudent[] = [];
    scoresAssignments: ScoresAssignment[] = [];
    scoresStudentMarks: ScoresStudentMark[] = [];
    assignmentsToStandards: AssignmentToStandard[] = [];
    standards: Standard[] = [];
    standardsStudentMarks: StandardsStudentMark[] = [];
    gradebookStudentAlerts: Alerts[] = [];
    userPermissions: UserPermission[];
    undoStudentMarks: GradebookUndoRedo[] = [];
    redoStudentMarks: GradebookUndoRedo[] = [];
    gradebookMismatchScores: GradebookMismatchScores[] = [];
    gradebookCurriculumStandards: GradebookCurriculumStandards[] = [];
    curriculumMarkTypes: CurriculumMarksType[];
    termGrades: TermGrades[] = [];
    validCurriculumMarkTypes = '';
    showTrends = false;
    showStudentNumbers = false;
    behaviorSocialSkillsTabName = 'Behavior / Social Skills';
    // Analysis Flags
    gradebookV9CategoryPointsSummary: GradebookCategoryPointsSummary[] = [];
    analysisTerm: AnalysisTerm[] = [];
    allTerms: Term[];
    allAssignments: Assignment[] = [];
    assignmentFormula: Formula[];
    analysisStudentAttendanceCount: AnalysisStudentsAttendance[];
    analysisSelectedStudent: GradebookStudent;
    studentDetailCallFromModel = false;

    private _isProgressCycleChecked: boolean;
    public get isProgressCycleChecked(): boolean {
        return this._isProgressCycleChecked;
    }

    public set isProgressCycleChecked(value: boolean) {
        this._isProgressCycleChecked = value;
        this.observableisProgressCycleChecked.next(value);
    }

    public observableisProgressCycleChecked = new Subject<boolean>();
    observableisProgressCycleCheckedStream$ = this.observableisProgressCycleChecked.asObservable();

    // --------------- Observable Objects -----------
    // -- Preferences
    gradebookPreferences: GradebookPreferences;
    observableGradebookPreferences = new Subject<GradebookPreferences>();
    observableGradebookPreferencesStream$ = this.observableGradebookPreferences.asObservable();
    // -- Recall Tab Change
    observableGradebookTabChange = new Subject<string>();
    observableGradebookTabChangeStream$ = this.observableGradebookTabChange.asObservable();
    // -- Gradebook Settings
    gradebookSettings: GradebookSettings;
    observableGradebookSettings = new Subject<GradebookSettings>();
    observableGradebookSettingsStream$ = this.observableGradebookSettings.asObservable();
    // -- Lookups
    observableLookups = new Subject<Term[]>();
    observableLookupsStream$ = this.observableLookups.asObservable();
    // -- Assignments
    assignments: Assignment[] = [];
    observableAssignments = new Subject<Assignment[]>();
    observableAssignmentsStream$ = this.observableAssignments.asObservable();
    observableAssignmentsSequence = new Subject();
    observableAssignmentsSequenceStream$ = this.observableAssignmentsSequence.asObservable();
    // -- Gradebook Students
    observableGradebookStudents = new Subject<GradebookStudent[]>();
    observableGradebookStudentsStream$ = this.observableGradebookStudents.asObservable();
    // -- Gradebook Scores Tab
    observableGradebookScoresTab = new Subject<ScoresStudentMark[]>();
    observableGradebookScoresTabStream$ = this.observableGradebookScoresTab.asObservable();
    // -- Gradebook Standards Tab
    observableGradebookStandardsTab = new Subject<AssignmentToStandard[]>();
    observableGradebookStandardsTabStream$ = this.observableGradebookStandardsTab.asObservable();
    // -- Indicator: Show / Hide
    observableGradebookIndicator = new Subject<boolean[]>();
    observableGradebookIndicatorStream$ = this.observableGradebookIndicator.asObservable();
    observableGradebookSGIndicator = new Subject<boolean[]>();
    observableGradebookSGIndicatorStream$ = this.observableGradebookSGIndicator.asObservable();
    // -- User Permission
    observableUserPermission = new Subject<UserPermission[]>();
    observableUserPermissionStream$ = this.observableUserPermission.asObservable();
    // -- Non-Scored Standards: Show / Hide
    observableGradebookNonScoredStandards = new Subject<boolean[]>();
    observableGradebookNonScoredStandardsStream$ = this.observableGradebookNonScoredStandards.asObservable();
    // -- Gradebook Special Marks
    observableGradebookSpecialMarks = new Subject<SpecialMark[]>();
    observableGradebookSpecialMarksStream$ = this.observableGradebookSpecialMarks.asObservable();
    // -- Gradebook Mismatch Scores
    observableGradebookMismatchScores = new Subject<GradebookMismatchScores[]>();
    observableGradebookMismatchScoresStream$ = this.observableGradebookMismatchScores.asObservable();
    // -- Assignment Save and New
    observableGradebookAssignmentSaveAndNew = new Subject<string>();
    observableGradebookAssignmentSaveAndNewStream$ = this.observableGradebookAssignmentSaveAndNew.asObservable();
    // -- Recalculate Standard Total Points
    observableGradebookRecalculateStandardTotalPoints = new Subject<string>();
    observableGradebookRecalculateStandardTotalPointsStream$ = this.observableGradebookRecalculateStandardTotalPoints.asObservable();
    // -- Regenerate Tools Menu
    observableGradebookToolsMenu = new Subject();
    observableGradebookToolsMenuStream$ = this.observableGradebookToolsMenu.asObservable();

    constructor(
        private http: HttpClient,
        private userService: UserService,
        private yearService: YearService,
        private userPreferencesService: UserPreferencesService,
        private sharedService: SharedService,
        private sharedGradeBookService: SharedGradebookService
    ) {
        this.currentUser = this.userService.currentUser;
        this.defaultYear = this.yearService.defaultYear;
        this.yearId = this.defaultYear.Id;
        this.schoolId = this.defaultYear.SchoolID;
        this.siteYearId = this.defaultYear.SiteYearId;
        this.selectedTab = 'Scores';
        this.selectedStandardTab = 'Standards for One Assignment';
        this.selectedReportingTab = 'Traditional';
        this.selectedReportingStandardTab = 'All Standards by Student';
        this.selectedAnalysisTab = 'Categories';
    }

    clearAllData() {
        this.gradebookSettings = null;
        this.showAssignment = false;
        this.showTrends = false;
        this.terms = [];
        this.categories = [];
        this.units = [];
        this.indicators = [];
        this.specialMarks = [];
        // this.gradeScaleDescriptions = [];
        this.gradebookStudents = [];
        this.gradebookStudentAlerts = [];
        this.undoStudentMarks = [];
        this.redoStudentMarks = [];
        this.gradebookMismatchScores = [];
        this.gradebookCurriculumStandards = [];
        this.curriculumMarkTypes = [];
        this.validCurriculumMarkTypes = '';
        this.defaultYear = this.yearService.defaultYear;
        this.yearId = this.defaultYear.Id;
        this.schoolId = this.defaultYear.SchoolID;
        this.siteYearId = this.defaultYear.SiteYearId;
        this.selectedStandardTab = 'Standards for One Assignment';
        this.selectedReportingTab = 'Traditional';
        this.selectedReportingStandardTab = 'All Standards by Student';
        this.selectedAnalysisTab = 'Categories';
        this.studentDetailCallFromModel = false;
        this.analysisTerm = [];
        this.allAssignments = [];
        this.assignmentFormula = [];
        this.gradebookV9CategoryPointsSummary = [];
        this.clearCourseData();
    }

    clearCourseData() {
        this.assignments = [];
        this.clearScoreData();
    }

    clearScoreData() {
        this.unsavedChanges = false;
        this.scoresAssignments = [];
        this.scoresStudentMarks = [];
        this.assignmentsToStandards = [];
        this.standards = [];
        this.standardsStudentMarks = [];
        this.updateGradebookV9GradebookScoresTab(this.scoresStudentMarks);
        this.updateGradebookV9GradebookStandardsTab(this.assignmentsToStandards);
        this.updateGradebookTabChange('');
    }

    padZero(assignmentSequence: number): string {
        let result = assignmentSequence.toString();
        while (result.length < 3) {
            result = '0' + result;
        }
        return result;
    }

    getEffectiveScore(assignment: ScoresAssignment, studentMark: ScoresStudentMark) {
        studentMark.ScorePercentValue = null;
        studentMark.EffectiveScoreValue = null;
        let specialMark: SpecialMark;
        let scorePercentValue: number;
        let effectiveScoreValue: number;

        if (studentMark.GradeBookSpecialMarkCodeId != null && studentMark.GradeBookSpecialMarkCodeId > 0) {
            specialMark = this.specialMarks.filter(o => o.Id == studentMark.GradeBookSpecialMarkCodeId)[0];
            if (specialMark != null) {
                studentMark.GradeBookSpecialMarkCode = specialMark.Code;
                studentMark.GradeBookSpecialMarkDescription = specialMark.Description;
                if (specialMark.Operation == 0 || (specialMark.Code == 'MSNG' && specialMark.Operation == 1 && specialMark.Value == 0)) {
                    if (studentMark.ScorePointValue != null) {
                        specialMark = null;
                        studentMark.GradeBookSpecialMarkCodeId = 0;
                        studentMark.GradeBookSpecialMarkCode = '';
                        studentMark.GradeBookSpecialMarkDescription = '';
                    }
                }
            }
        }

        if (studentMark.ScorePointValue == null && (studentMark.GradeBookSpecialMarkCodeId == null || studentMark.GradeBookSpecialMarkCodeId == 0)) {
            effectiveScoreValue = null;
        } else {
            if (assignment.BasePoints == null || assignment.BasePoints == 0) {
                scorePercentValue = null;
                effectiveScoreValue = studentMark.ScorePointValue;
            } else if (studentMark.GradeBookSpecialMarkCodeId == null || studentMark.GradeBookSpecialMarkCodeId == 0) {
                if (!isNaN(studentMark.ScorePointValue / assignment.BasePoints)) {
                    scorePercentValue = parseFloat(((((studentMark.ScorePointValue * 100) / assignment.BasePoints) * 100) / 100).toFixed(this.countDecimals(studentMark.ScorePointValue)));
                    effectiveScoreValue = parseFloat(((((studentMark.ScorePointValue * 100) / assignment.BasePoints) * assignment.BasePoints * 0.01 * 100) / 100).toFixed(this.countDecimals(studentMark.ScorePointValue)));
                }
            } else if (studentMark.GradeBookSpecialMarkCodeId != null && studentMark.GradeBookSpecialMarkCodeId > 0) {
                if (specialMark != null) {
                    if (specialMark.Operation == 1) {
                        if (!isNaN(specialMark.Value / assignment.BasePoints)) {
                            scorePercentValue = parseFloat((((specialMark.Value * 100 / assignment.BasePoints) * 100) / 100).toFixed(this.countDecimals(studentMark.ScorePointValue)));
                            effectiveScoreValue = parseFloat(((specialMark.Value * 100 / assignment.BasePoints * assignment.BasePoints * 0.01 * 100) / 100).toFixed(this.countDecimals(studentMark.ScorePointValue)));
                        }
                    } else if (specialMark.Operation == 2) {
                        if (!isNaN(studentMark.ScorePointValue * specialMark.Value / assignment.BasePoints)) {
                            scorePercentValue = parseFloat((((studentMark.ScorePointValue * specialMark.Value / assignment.BasePoints) * 100) / 100).toFixed(this.countDecimals(studentMark.ScorePointValue)));
                            effectiveScoreValue = parseFloat((((studentMark.ScorePointValue * specialMark.Value / assignment.BasePoints) * assignment.BasePoints * 0.01 * 100) / 100).toFixed(this.countDecimals(studentMark.ScorePointValue)));
                        }
                    } else if (specialMark.Operation == 3) {
                        if (studentMark.ScorePointValue == undefined) {
                            studentMark.ScorePointValue = 0;
                        }
                        if (!isNaN((studentMark.ScorePointValue + specialMark.Value) * 100 / assignment.BasePoints)) {
                            scorePercentValue = parseFloat(((((studentMark.ScorePointValue + specialMark.Value) * 100 / assignment.BasePoints) * 100) / 100).toFixed(this.countDecimals(studentMark.ScorePointValue)));
                            effectiveScoreValue = parseFloat(((((studentMark.ScorePointValue + specialMark.Value) * 100 / assignment.BasePoints) * assignment.BasePoints * 0.01 * 100) / 100).toFixed(this.countDecimals(studentMark.ScorePointValue)));
                        }
                    }
                }
            }
        }

        studentMark.EffectiveScoreValue = effectiveScoreValue != null ? effectiveScoreValue.toString() : studentMark.EffectiveScoreValue;
        if (scorePercentValue != null) {
            // scorePercentValue = this.gradebookSettings.CalculateSGColumnsPercentsRoundUp ? Math.round(scorePercentValue) : scorePercentValue;
            studentMark.ScorePercentValue = scorePercentValue + '%';
        }
        this.setIndicators(assignment, studentMark, specialMark);
    }

    countDecimals(value: number) {
        if (!value) {
            return 0;
        }
        // else if (Math.floor(value) === value) {
        //     return 0;
        // }
        // return value.toString().split('.')[1].length || 0;
        return 2;
    }

    setIndicators(assignment: ScoresAssignment, studentMark: ScoresStudentMark, specialMark: SpecialMark = null) {
        if (assignment != null && studentMark != null) {
            if (studentMark.GradeBookStudentMarkId > 0 && assignment.UseObjectivesForPoints == false && studentMark.InvalidMark == false) {
                studentMark.ScoreBackgroundColor = '';
                studentMark.ScoreForeColor = '';

                let effectiveScore = parseFloat(studentMark.ScorePercentValue);
                if (isNaN(effectiveScore) == true && assignment.SystemGrade == true && studentMark.ScorePercentValue != null) {
                    const scorePercentValue = studentMark.ScorePercentValue.split(' ');
                    if (scorePercentValue.length == 2) {
                        effectiveScore = parseFloat(scorePercentValue[1].replace('%', ''));
                    }
                }

                if (isNaN(effectiveScore) == false) {
                    const indicators = this.indicators;
                    if (indicators.length > 0) {
                        const assignmentIndicators = indicators.filter(o => o.AssignmentIndicator == true && (this.showIndicators == true || o.Visible == true) && (o.AssignmentId == assignment.GradeBookAssignmentId || (assignment.SystemGrade == false && o.AssignmentId == 0) || (assignment.SystemGrade == true && o.SystemGradeFinal == true)));
                        const categoryIndicators = indicators.filter(o => o.AssignmentIndicator == false && (this.showIndicators == true || o.Visible == true) && (o.CategoryId == assignment.GradeBookAssignmentTypeId || (assignment.SystemGrade == false && o.CategoryId == 0) || (assignment.SystemGrade == true && o.SystemGradeFinal == true)));
                        if (assignmentIndicators.length > 0 || categoryIndicators.length > 0) {
                            const assignmentIndicator = assignmentIndicators.filter(i => i.FromRange <= effectiveScore && i.ToRange >= effectiveScore)[0];
                            if (assignmentIndicator != null) {
                                studentMark.ScoreBackgroundColor = assignmentIndicator.BackgroundColor;
                                studentMark.ScoreForeColor = assignmentIndicator.ForegroundColor;
                            } else {
                                const categoryIndicator = categoryIndicators.filter(i => i.FromRange <= effectiveScore && i.ToRange >= effectiveScore)[0];
                                if (categoryIndicator != null) {
                                    studentMark.ScoreBackgroundColor = categoryIndicator.BackgroundColor;
                                    studentMark.ScoreForeColor = categoryIndicator.ForegroundColor;
                                }
                            }
                        }
                    }
                }

                if (studentMark.ScoreBackgroundColor == '') {
                    if (specialMark == null && studentMark.GradeBookSpecialMarkCodeId != null && studentMark.GradeBookSpecialMarkCodeId > 0) {
                        specialMark = this.specialMarks.filter(o => o.Id == studentMark.GradeBookSpecialMarkCodeId)[0];
                    }
                    if (specialMark != null && specialMark.ColorCode != '') {
                        studentMark.ScoreBackgroundColor = specialMark.ColorCode;
                    }
                }
            }
        }
    }

    updateGradebookPreferences(gradebookPreferences: GradebookPreferences) {
        this.gradebookPreferences = gradebookPreferences;
        if (this.gradebookPreferences != undefined && this.gradebookPreferences.DefaultForStandardsView == 'Show') {
            this.showAssignment = true;
        } else {
            this.showAssignment = false;
        }

        if (this.gradebookPreferences != null) {
            if (this.gradebookPreferences.NonScrollingColumns.length > 0 && this.gradebookPreferences.NonScrollingColumns[0] == null) {
                this.gradebookPreferences.NonScrollingColumns = [];
            }

            if (this.gradebookPreferences.NonScrollingColumns.length == 0 && this.gradebookPreferences.ScrollingColumns.length == 0) {
                this.gradebookPreferences.NonScrollingColumns.push({
                    Field: 'StudentName',
                    DisplayName: 'Student Name',
                    width: '280px'
                });
            }
        }

        this.observableGradebookPreferences.next(this.gradebookPreferences);
    }

    updateUserPermission(userPermissions: UserPermission[]) {
        this.userPermissions = userPermissions;
        this.observableUserPermission.next(userPermissions);
    }

    updateGradebookTabChange(selectedTab: string) {
        this.observableGradebookTabChange.next(selectedTab);
    }

    saveGradebookSelectionPreferences() {
        const xmlStream: string[] = [];
        xmlStream.push(JSON.stringify(this.userPreferencesService.gradebookV9SelectionPreferences));
        this.userPreferencesService.saveUserPreferences(0, '/OneSISGradebookSelectionPreferences', xmlStream, 0, '').subscribe();
    }

    saveGradebookAnalysisSettings() {
        const xmlStream: string[] = [];
        xmlStream.push(JSON.stringify(this.userPreferencesService.gradebookV9AnalysisSettings));
        this.userPreferencesService.saveUserPreferences(0, '/OneSISGradebookAnalysisSettings', xmlStream, 0, '').subscribe();
    }

    getGradebookV9Settings(): Observable<GradebookSettings> {
        let queryString = '';
        queryString += '?siteSectionId=' + this.course.Id;
        return this.http.get<GradebookSettings>(System.apiURL.Classroom + 'GetGradebookV9Settings' + queryString);
    }

    updateGradebookV9Settings(gradebookSettings: GradebookSettings) {
        this.gradebookSettings = gradebookSettings;
        this.observableGradebookSettings.next(gradebookSettings);
    }

    gradebookRecalculateAssignments(): Observable<boolean> {
        let queryString = '';
        queryString += '?siteSectionId=' + this.course.Id;
        return this.http.get<boolean>(System.apiURL.Classroom + 'GradebookRecalculateAssignments' + queryString);
    }

    getGradebookV9Terms(yearId: number, schoolId: number, siteSectionId: number, includeAllTerms: boolean): Observable<Term[]> {
        let queryString = '';
        queryString += '?yearId=' + yearId +
            '&schoolId=' + schoolId +
            '&siteSectionId=' + siteSectionId +
            '&includeAllTerms=' + includeAllTerms;
        return this.http.get<Term[]>(System.apiURL.Classroom + 'GetGradebookV9Terms' + queryString);
    }

    getGradebookV9Categories(): Observable<Category[]> {
        let queryString = '';
        queryString += '?yearId=' + this.yearId +
            '&siteSectionId=' + this.course.Id;
        return this.http.get<Category[]>(System.apiURL.Classroom + 'GetGradebookV9Categories' + queryString);
    }

    getGradebookV9CategorySequence(categoryId: number): Observable<number> {
        let queryString = '';
        queryString += '?siteSectionId=' + this.course.Id +
            '&gradeBookAssignmentTypeId=' + categoryId;
        return this.http.get<number>(System.apiURL.Classroom + 'GetGradebookV9CategorySequence' + queryString);
    }

    saveGradebookV9Categories(data: Category[]) {
        return this.http.post(System.apiURL.Classroom + 'SaveGradebookV9Categories', data);
    }

    getGradebookV9Units(siteSectionId: number): Observable<Unit[]> {
        let queryString = '';
        queryString += '?yearId=' + this.yearId +
            '&schoolId=' + this.schoolId +
            '&siteSectionId=' + siteSectionId;
        return this.http.get<Unit[]>(System.apiURL.Classroom + 'GetGradebookV9Units' + queryString);
    }

    getGradebookV9SpecialMarks(): Observable<SpecialMark[]> {
        let queryString = '';
        queryString += '?siteYearId=' + this.siteYearId +
            '&siteSectionId=' + this.course.Id;
        return this.http.get<SpecialMark[]>(System.apiURL.Classroom + 'GetGradebookV9SpecialMarks' + queryString);
    }

    updateGradebookV9Lookups(gradebookTerms: Term[]) {
        this.terms = gradebookTerms;
        this.observableLookups.next(gradebookTerms);
    }

    updateGradebookV9SpecialMarks(specialMarks: SpecialMark[]) {
        this.specialMarks = specialMarks;
        this.observableGradebookSpecialMarks.next(specialMarks);
    }

    saveStudentSpecialGradeScale(studentId: number, gradeScaleDescriptionId: number) {
        let queryString = '';
        queryString += '?studentId=' + studentId +
            '&siteYearId=' + this.siteYearId +
            '&siteSectionId=' + this.course.Id +
            '&gradeScaleDescriptionId=' + gradeScaleDescriptionId;
        return this.http.get(System.apiURL.Classroom + 'SaveStudentSpecialGradeScale' + queryString);
    }

    getGradebookV9Assignments(siteSectionId: number, termId: number, assignmentTypeId: number, gradebookUnitId: number, assignmentId: number): Observable<Assignment[]> {
        let queryString = '';
        queryString += '?siteSectionId=' + siteSectionId +
            '&termId=' + termId +
            '&assignmentTypeId=' + assignmentTypeId +
            '&gradebookUnitId=' + gradebookUnitId +
            '&gradebookAssignmentId=' + assignmentId;
        return this.http.get<Assignment[]>(System.apiURL.Classroom + 'GetGradebookV9Assignments' + queryString);
    }

    updateGradebookV9Assignments(gradebookAssignments: Assignment[]) {
        this.assignments = gradebookAssignments;
        this.observableAssignments.next(gradebookAssignments);
    }

    getGradebookV9AssignmentHomework(assignmentId: number): Observable<string> {
        let queryString = '';
        queryString += '?gradebookAssignmentId=' + assignmentId;
        return this.http.get<string>(System.apiURL.Classroom + 'GetGradebookV9AssignmentHomework' + queryString);
    }

    getGradebookV9Attachments(assignmentId: number): Observable<GradebookAttachment[]> {
        let queryString = '';
        queryString += '?gradebookAssignmentId=' + assignmentId;
        return this.http.get<GradebookAttachment[]>(System.apiURL.Classroom + 'GetGradebookV9Attachments' + queryString);
    }

    getGradebookV9CurriculumStandards(assignmentId: number): Observable<GradebookCurriculumStandards[]> {
        let queryString = '';
        queryString += '?yearId=' + this.yearId +
            '&siteSectionId=' + this.course.Id +
            '&districtSubjectId=' + this.course.SubjectId +
            '&gradebookAssignmentId=' + assignmentId;
        return this.http.get<GradebookCurriculumStandards[]>(System.apiURL.Classroom + 'GetGradebookV9CurriculumStandards' + queryString);
    }

    saveGradebookV9Assignments(copyToAssignments: number, recurrenceOption: string, recurrenceDuration: string, recurrenceWeeks: number, recurrenceUntilDate: string, siteSectionIds: string, data: Assignment[]): Observable<string> {
        let queryString = '';
        queryString += '?districtSubjectId=' + this.course.SubjectId +
            '&copyToAssignments=' + copyToAssignments +
            '&recurrenceOption=' + recurrenceOption +
            '&recurrenceDuration=' + recurrenceDuration +
            '&recurrenceWeeks=' + recurrenceWeeks +
            '&recurrenceUntilDate=' + recurrenceUntilDate +
            '&siteSectionIds=' + siteSectionIds;
        return this.http.post<string>(System.apiURL.Classroom + 'SaveGradebookV9Assignments' + queryString, data);
    }

    saveGradebookV9AssignmentShowOnPortal(assignmentId: number, showOnPortal: boolean) {
        let queryString = '';
        queryString += '?gradebookAssignmentId=' + assignmentId +
            '&showOnPortal=' + showOnPortal;
        return this.http.get(System.apiURL.Classroom + 'SaveGradebookV9AssignmentShowOnPortal' + queryString);
    }

    deleteGradebookV9Assignment(assignmentId: number) {
        let queryString = '';
        queryString += '?gradebookAssignmentId=' + assignmentId +
            '&siteSectionId=' + this.course.Id;
        return this.http.get(System.apiURL.Classroom + 'DeleteGradebookV9Assignment' + queryString);
    }

    getGradebookV9Formula(assignmentId: number): Observable<Formula[]> {
        let queryString = '';
        queryString += '?gradebookAssignmentId=' + assignmentId +
            '&siteSectionId=' + this.course.Id;
        return this.http.get<Formula[]>(System.apiURL.Classroom + 'GetGradebookV9Formula' + queryString);
    }

    saveGradebookV9Formula(assignmentId: number, formula: Formula) {
        let queryString = '';
        queryString += '?gradebookAssignmentId=' + assignmentId;
        return this.http.post(System.apiURL.Classroom + 'SaveGradebookV9Formula' + queryString, formula);
    }

    getGradebookV9Student(): Observable<GradebookStudent[]> {
        let queryString = '';
        queryString += '?schoolId=' + this.schoolId +
            '&yearId=' + this.yearId +
            '&siteSectionId=' + this.course.Id +
            '&termId=' + this.selectedTerm.Id;
        return this.http.get<GradebookStudent[]>(System.apiURL.Classroom + 'GetGradebookV9Student' + queryString);
    }

    updateGradebookV9GradebookStudents(students: GradebookStudent[]) {
        this.gradebookStudents = students;
        this.observableGradebookStudents.next(students);
    }

    getGradebookV9ScoresAssignments(gradebookAssignmentId: number): Observable<ScoresAssignment[]> {
        let queryString = '';
        queryString += '?schoolId=' + this.schoolId +
            '&yearId=' + this.yearId +
            '&siteSectionId=' + this.course.Id +
            '&termId=' + this.selectedTerm.Id +
            '&assignmentTypeId=' + this.selectedCategory.Id +
            '&gradebookUnitId=' + this.selectedUnit.Id +
            '&gradebookAssignmentId=' + gradebookAssignmentId;
        return this.http.get<ScoresAssignment[]>(System.apiURL.Classroom + 'GetGradebookV9ScoresAssignments' + queryString);
    }

    getGradebookV9ScoresStudentMarks(gradebookAssignmentId: number, studentId: number, assignmentTypeId: number): Observable<ScoresStudentMark[]> {
        let queryString = '';
        queryString += '?schoolId=' + this.schoolId +
            '&yearId=' + this.yearId +
            '&siteSectionId=' + this.course.Id +
            '&termId=' + this.selectedTerm.Id +
            '&assignmentTypeId=' + assignmentTypeId +
            '&gradebookUnitId=' + this.selectedUnit.Id +
            '&gradebookAssignmentId=' + gradebookAssignmentId +
            '&studentId=' + studentId;
        return this.http.get<ScoresStudentMark[]>(System.apiURL.Classroom + 'GetGradebookV9ScoresStudentMarks' + queryString);
    }

    updateGradebookV9GradebookScoresTab(scoresStudentMarks: ScoresStudentMark[]) {
        this.scoresStudentMarks = scoresStudentMarks;
        this.observableGradebookScoresTab.next(scoresStudentMarks);
    }

    saveGradebookV9ScoresStudentMarks(studentMarks: ScoresStudentMark[]) {
        let queryString = '';
        queryString += '?siteYearId=' + this.siteYearId +
            '&siteSectionId=' + this.course.Id;
        return this.http.post(System.apiURL.Classroom + 'SaveGradebookV9ScoresStudentMarks' + queryString, studentMarks);
    }

    saveGradebookV9StudentScoreComment(gradebookStudentMarkId: number, comment: string[]) {
        let queryString = '';
        queryString += '?gradebookStudentMarkId=' + gradebookStudentMarkId;
        return this.http.post(System.apiURL.Classroom + 'SaveGradebookV9StudentScoreComment' + queryString, comment);
    }

    getGradebookV9StudentMarksByStudentId(studentId: number, gradeBookAssignmentId: number, data: string[]): Observable<GradebookStudentMarkByStudentId[]> {
        let queryString = '';
        queryString += '?siteYearId=' + this.siteYearId +
            '&studentId=' + studentId +
            '&gradeBookAssignmentId=' + gradeBookAssignmentId +
            '&termDefinitionId=' + this.selectedTerm.Id +
            '&siteSectionId=' + this.course.Id;
        return this.http.post<GradebookStudentMarkByStudentId[]>(System.apiURL.Classroom + 'GetGradebookV9StudentMarksByStudentId' + queryString, data);
    }

    getGradebookV9AssignmentsToStandards(assignmentId: number): Observable<AssignmentToStandard[]> {
        let queryString = '';
        queryString += '?siteSectionId=' + this.course.Id +
            '&termId=' + this.selectedTerm.Id +
            '&assignmentTypeId=' + this.selectedCategory.Id +
            '&gradebookUnitId=' + this.selectedUnit.Id +
            '&gradebookAssignmentId=' + assignmentId;
        return this.http.get<AssignmentToStandard[]>(System.apiURL.Classroom + 'GetGradebookV9AssignmentsToStandards' + queryString);
    }

    getGradebookV9Standards(assignmentId: number): Observable<Standard[]> {
        let queryString = '';
        queryString += '?siteSectionId=' + this.course.Id +
            '&termId=' + this.selectedTerm.Id +
            '&assignmentTypeId=' + this.selectedCategory.Id +
            '&gradebookUnitId=' + this.selectedUnit.Id +
            '&gradebookAssignmentId=' + assignmentId;
        return this.http.get<Standard[]>(System.apiURL.Classroom + 'GetGradebookV9Standards' + queryString);
    }

    getGradebookV9StandardsStudentMarks(assignmentId: number): Observable<StandardsStudentMark[]> {
        let queryString = '';
        queryString += '?siteSectionId=' + this.course.Id +
            '&termId=' + this.selectedTerm.Id +
            '&assignmentTypeId=' + this.selectedCategory.Id +
            '&gradebookUnitId=' + this.selectedUnit.Id +
            '&gradebookAssignmentId=' + assignmentId;
        return this.http.get<StandardsStudentMark[]>(System.apiURL.Classroom + 'GetGradebookV9StandardsStudentMarks' + queryString);
    }

    updateGradebookV9GradebookStandardsTab(assignmentsToStandards: AssignmentToStandard[]) {
        this.assignmentsToStandards = assignmentsToStandards;
        this.observableGradebookStandardsTab.next(assignmentsToStandards);
    }

    saveGradebookV9StandardsStudentMarks(data: StandardsStudentMark[]) {
        let queryString = '';
        queryString += '?siteSectionId=' + this.course.Id;
        return this.http.post(System.apiURL.Classroom + 'SaveGradebookV9StandardsStudentMarks' + queryString, data);
    }

    getGradebookV9StandardExportBase64(tabName: string, subTabName: string): Observable<string> {
        const selectedStandardStudentId = this.selectedStandardStudentId != undefined ? this.selectedStandardStudentId : 0;
        const selectedStandardAssignmentId = this.selectedStandardAssignmentId != undefined ? this.selectedStandardAssignmentId : 0;
        const courseObjectiveId = this.selectedStandardId != undefined ? this.selectedStandardId : 0;
        const StandardAnalysisType = this.userPreferencesService.gradebookV9SelectionPreferences.StandardAnalysisType;

        let queryString = '';
        queryString += '?schoolId=' + this.schoolId +
            '&yearId=' + this.yearId +
            '&siteSectionId=' + this.course.Id +
            '&termId=' + this.selectedTerm.Id +
            '&assignmentTypeId=' + this.selectedCategory.Id +
            '&studentId=' + selectedStandardStudentId +
            '&assignmentId=' + selectedStandardAssignmentId +
            '&courseObjectiveId=' + courseObjectiveId +
            '&gradebookUnitId=' + this.selectedUnit.Id +
            '&tabName=' + tabName +
            '&subTabName=' + subTabName +
            '&standardAnalysisType=' + StandardAnalysisType;
        return this.http.get<string>(System.apiURL.Classroom + 'GetGradebookV9StandardExportBase64' + queryString);
    }

    getGradebookV9RCGExportBase64(tabName: string, subTabName: string): Observable<string> {
        let selectedStandardStudentId = this.selectedStandardStudentId != undefined ? this.selectedStandardStudentId : 0;
        const selectedStandardAssignmentId = this.selectedStandardAssignmentId != undefined ? this.selectedStandardAssignmentId : 0;
        let courseObjectiveId = this.selectedStandardId != undefined ? this.selectedStandardId : 0;
        const StandardAnalysisType = this.userPreferencesService.gradebookV9SelectionPreferences.StandardAnalysisType;

        let termId = 0;
        if (tabName == 'Standards' && subTabName == 'All Standards by Student') {
            selectedStandardStudentId = this.userPreferencesService.gradebookV9SelectionPreferences.RCGStandardByStudentId;
            termId = this.userPreferencesService.gradebookV9SelectionPreferences.RCGStandardByStudentTermId;
        } else if (tabName == 'Standards' && subTabName == 'All Students by Standard') {
            courseObjectiveId = this.userPreferencesService.gradebookV9SelectionPreferences.RCGStudentByStandardId;
            termId = this.userPreferencesService.gradebookV9SelectionPreferences.RCGStudentByStandardTermId;
        }


        let queryString = '';
        queryString += '?schoolId=' + this.schoolId +
            '&yearId=' + this.yearId +
            '&siteSectionId=' + this.course.Id +
            '&termId=' + termId +
            '&assignmentTypeId=' + this.selectedCategory.Id +
            '&studentId=' + selectedStandardStudentId +
            '&assignmentId=' + selectedStandardAssignmentId +
            '&courseObjectiveId=' + courseObjectiveId +
            '&gradebookUnitId=' + this.selectedUnit.Id +
            '&tabName=' + tabName +
            '&subTabName=' + subTabName +
            '&standardAnalysisType=' + StandardAnalysisType;
        return this.http.get<string>(System.apiURL.Classroom + 'GetGradebookV9RCGExportBase64' + queryString);
    }

    getGradebookV9ExportBase64(): Observable<string> {
        let queryString = '';
        queryString += '?schoolId=' + this.schoolId +
            '&yearId=' + this.yearId +
            '&siteSectionId=' + this.course.Id +
            '&termId=' + this.selectedTerm.Id +
            '&assignmentTypeId=' + this.selectedCategory.Id +
            '&gradebookUnitId=' + this.selectedUnit.Id;
        return this.http.get<string>(System.apiURL.Classroom + 'GetGradebookV9ExportBase64' + queryString);
    }

    saveGradebookV9CopyAssignments(data: string[]): Observable<string> {
        return this.http.post<string>(System.apiURL.Classroom + 'SaveGradebookV9CopyAssignments', data);
    }

    getGradebookV9BackupSets(backupId: number): Observable<GradebookBackup[]> {
        let queryString = '';
        queryString += '?siteSectionId=' + this.course.Id +
            '&backupId=' + backupId;
        return this.http.get<GradebookBackup[]>(System.apiURL.Classroom + 'GetGradebookV9BackupSets' + queryString);
    }

    createGradebookV9Backup(backupCreateNewOption: number, backupCreateNewText: string): Observable<string> {
        let queryString = '';
        queryString += '?siteSectionId=' + this.course.Id +
            '&siteYearId=' + this.siteYearId +
            '&backupCreateNewOption=' + backupCreateNewOption +
            '&backupCreateNewText=' + backupCreateNewText;
        return this.http.get<string>(System.apiURL.Classroom + 'CreateGradebookV9Backup' + queryString);
    }

    restoreGradebookV9Backup(ignoreExceedDate: boolean, attachment: GradebookBackup): Observable<string> {
        let queryString = '';
        queryString += '?yearId=' + this.yearId +
            '&schoolId=' + this.schoolId +
            '&siteSectionId=' + this.course.Id +
            '&ignoreExceedDate=' + ignoreExceedDate;
        return this.http.post<string>(System.apiURL.Classroom + 'RestoreGradebookV9Backup' + queryString, attachment);
    }

    deleteGradebookV9BackupSet(backupId: number): Observable<string> {
        let queryString = '';
        queryString += '?backupId=' + backupId;
        return this.http.get<string>(System.apiURL.Classroom + 'DeleteGradebookV9BackupSet' + queryString);
    }

    gradebookV9FormulaCopy(desinationChoice: number, assignmentIds: number[]) {
        let queryString = '';
        queryString += '?siteYearId=' + this.siteYearId +
            '&siteSectionId=' + this.course.Id +
            '&desinationChoice=' + desinationChoice;
        return this.http.post<string>(System.apiURL.Classroom + 'GradebookV9FormulaCopy' + queryString, assignmentIds);
    }

    getGradebookV9SystemGradeAssignments(showUnmatchedFormulaAssignments: boolean): Observable<GradebookSystemGradeAssignment[]> {
        let queryString = '';
        queryString += '?siteYearId=' + this.siteYearId +
            '&siteSectionId=' + this.course.Id +
            '&termId=' + this.selectedTerm.Id +
            '&showUnmatchedFormulaAssignments=' + showUnmatchedFormulaAssignments;
        return this.http.get<GradebookSystemGradeAssignment[]>(System.apiURL.Classroom + 'GetGradebookV9SystemGradeAssignments' + queryString);
    }

    gradebookV9ResetFormulas() {
        let queryString = '';
        queryString += '?siteYearId=' + this.siteYearId +
            '&siteSectionId=' + this.course.Id;
        return this.http.get(System.apiURL.Classroom + 'GradebookV9ResetFormulas' + queryString);
    }

    importObjMarksFromGradebookToStudentCurriculumMarks() {
        let queryString = '';
        queryString += '?siteYearId=' + this.siteYearId +
            '&currentTermCycleId=' + this.gradebookSettings.CurrentGradeCycleId +
            '&siteSectionId=' + this.course.Id;
        return this.http.get(System.apiURL.Classroom + 'ImportObjMarksFromGradebookToStudentCurriculumMarks' + queryString);
    }

    saveGradebookV9SpecialMarks(data: SpecialMark[]): Observable<string> {
        let queryString = '';
        queryString += '?siteYearId=' + this.siteYearId;
        return this.http.post<string>(System.apiURL.Classroom + 'SaveGradebookV9SpecialMarks' + queryString, data);
    }

    saveGradebookV9Units(gradebookUnits: Unit[]) {
        return this.http.post<string>(System.apiURL.Classroom + 'SaveGradebookV9Units', gradebookUnits);
    }

    deleteGradebookV9Unit(unitId: number) {
        let queryString = '';
        queryString += '?unitId=' + unitId;
        return this.http.get(System.apiURL.Classroom + 'DeleteGradebookV9Unit' + queryString);
    }

    saveGradebookV9StudentSequence(gradebookStudents: GradebookStudent[]) {
        let queryString = '';
        queryString += '?siteYearId=' + this.siteYearId +
            '&siteSectionId=' + this.course.Id;
        return this.http.post(System.apiURL.Classroom + 'SaveGradebookV9StudentSequence' + queryString, gradebookStudents);
    }

    getGradebookV9CategoryPointsSummary(): Observable<GradebookCategoryPointsSummary[]> {
        let queryString = '';
        queryString += '?siteYearId=' + this.siteYearId +
            '&siteSectionId=' + this.course.Id;
        return this.http.get<GradebookCategoryPointsSummary[]>(System.apiURL.Classroom + 'GetGradebookV9CategoryPointsSummary' + queryString);
    }

    getGradebookV9MismatchScores(): Observable<GradebookMismatchScores[]> {
        const termId = this.selectedTerm == undefined ? 0 : this.selectedTerm.Id;
        let queryString = '';
        queryString += '?schoolId=' + this.schoolId +
            '&yearId=' + this.yearId +
            '&siteSectionId=' + this.course.Id +
            '&termId=' + termId;
        return this.http.get<GradebookMismatchScores[]>(System.apiURL.Classroom + 'GetGradebookV9MismatchScores' + queryString);
    }

    updateGradebookV9MismatchScores(gradebookMismatchScores: GradebookMismatchScores[]) {
        this.gradebookMismatchScores = gradebookMismatchScores;
        this.observableGradebookMismatchScores.next(gradebookMismatchScores);
    }

    gradebookV9CalculateGPA(data: string[]) {
        let queryString = '';
        queryString += '?siteYearId=' + this.siteYearId +
            '&siteSectionId=' + this.course.Id;
        return this.http.post(System.apiURL.Classroom + 'GradebookV9CalculateGPA' + queryString, data);
    }

    // -------------------------- Other ---------------
    getSiteSectionByStudentId(studentId: number): Observable<ClassroomSummary[]> {
        let queryString = '';
        queryString += '?siteYearId=' + this.siteYearId +
            '&siteSectionId=' + this.course.Id +
            '&studentId=' + studentId;
        return this.http.get<ClassroomSummary[]>(System.apiURL.Classroom + 'GetSiteSectionByStudentId' + queryString);
    }

    // -- TODO: Need to rewrite
    getGradeBookAssignmentByStudentId(selectedCourseId: number, studentId: number): Observable<Assignment[]> {
        let queryString = '';
        queryString += '?courseId=' + this.course.Id +
            '&siteSectionId=' + selectedCourseId +
            '&studentId=' + studentId;
        return this.http.get<Assignment[]>(System.apiURL.Classroom + 'GetGradeBookAssignmentReadByStudentId' + queryString);
    }

    // -- TODO: Need to rewrite
    gradeBookStudentMarksCopy(studentId: number, data: number[]) {
        let queryString = '';
        queryString += '?siteYearId=' + this.siteYearId +
            '&siteSectionId=' + this.course.Id +
            '&studentId=' + studentId;
        return this.http.post(System.apiURL.Classroom + 'GradeBookStudentMarksCopy' + queryString, data);
    }

    // Report Card Grades //

    getReportCardGradesData(importGrades: boolean, importScoresForSeniorsOnly = false): Observable<ReportCardGrades[]> {
        let queryString = '';
        if (this.gradebookSettings != undefined) {
            queryString += '?siteYearId=' + this.siteYearId +
                '&courseId=' + this.course.Id +
                '&importGrades=' + importGrades +
                '&currentTermCycleId=' + this.gradebookSettings.CurrentGradeCycleId +
                '&importScoresForSeniorsOnly=' + importScoresForSeniorsOnly;
            return this.http.get<ReportCardGrades[]>(System.apiURL.Classroom + 'GetReportCardGradesData' + queryString);
        }
        else {
            return of(null);
        }
    }

    getTermGradeListCommentLookup(includeInactive: boolean): Observable<TermGradeCodes[]> {
        let queryString = '';
        queryString += '?schoolId=' + this.schoolId +
            '&includeInactive=' + includeInactive;
        return this.http.get<TermGradeCodes[]>(System.apiURL.Classroom + 'GetTermGradeListCommentLookup' + queryString);
    }

    saveReportCardGrades(usingPercentageGrades: boolean, termGrades: TermGrades[]): Observable<boolean> {
        let queryString = '';
        queryString += '?siteYearId=' + this.siteYearId +
            '&currentTermCycleId=' + this.gradebookSettings.CurrentGradeCycleId +
            '&courseId=' + this.course.Id +
            '&usingPercentageGrades=' + usingPercentageGrades;
        return this.http.post<boolean>(System.apiURL.Classroom + 'SaveReportCardGrades' + queryString, termGrades);
    }

    getBehaviorSkillRecordForAllTermGradeCycles(studentId: string, usingPercentageGrades: boolean, curriculumHeaderTermType: TermgradeCycleHeader[]): Observable<RCGBehaviourSkill[]> {
        let queryString = '';
        queryString = '?siteYearId=' + this.siteYearId +
            '&courseId=' + this.course.Id +
            '&studentId=' + studentId +
            '&usingPercentageGrades=' + usingPercentageGrades +
            '&currentTermCycleId=' + this.gradebookSettings.CurrentGradeCycleId;
        return this.http.post<RCGBehaviourSkill[]>(System.apiURL.Classroom + 'GetBehaviorSkillRecordForAllTermGradeCycles' + queryString, curriculumHeaderTermType);
    }

    getCurriculumnMarksandStandards(termGrades: TermGrades[]): Observable<RCGStandard[]> {
        let queryString = '';
        queryString = '?siteYearId=' + this.siteYearId +
            '&yearId=' + this.yearId +
            '&courseId=' + this.course.Id +
            '&currentTermCycleId=' + this.gradebookSettings.CurrentGradeCycleId;
        return this.http.post<RCGStandard[]>(System.apiURL.Classroom + 'GetCurriculumnMarksandStandards' + queryString, termGrades);
    }

    getCurriculumMarkTypesReadForSubject(): Observable<CurriculumMarksType[]> {
        return this.sharedService.getCurriculumMarkTypesReadForSubject(this.course.Id);
    }

    getCurriculumStudentsMarks() {
        let queryString = '';
        queryString = '?siteSectionId=' + this.course.Id;
        return this.http.get(System.apiURL.Classroom + 'GetCurriculumStudentsMarks' + queryString);
    }

    getBehaviorSkillRecord(usingPercentageGrades: boolean, termGrades: TermGrades[]): Observable<RCGBehaviourSkill[]> {
        let queryString = '';
        queryString += '?courseId=' + this.course.Id +
            '&usingPercentageGrades=' + usingPercentageGrades;
        return this.http.post<RCGBehaviourSkill[]>(System.apiURL.Classroom + 'GetBehaviorSkillRecord' + queryString, termGrades);
    }

    getReportcardViewComments(termId: number, C360GradebookLimitNumberOfComments: boolean, currentDate: Date, forAssignments: boolean, termCommentStudentId: number) {
        let queryString = '';
        queryString += '?yearId=' + this.yearId +
            '&schoolId=' + this.schoolId +
            '&siteSectionId=' + this.course.Id +
            '&termId=' + termId +
            '&C360GradebookLimitNumberOfComments=' + C360GradebookLimitNumberOfComments +
            '&currentDate=' + currentDate +
            '&forAssignments=' + forAssignments +
            '&termCommentStudentId=' + termCommentStudentId;
        return this.http.get(System.apiURL.Classroom + 'GetGradingPeriodComments' + queryString);
    }

    getGradingPeriodComments(termId, C360GradebookLimitNumberOfComments, currentDate, forAssignments, termCommentStudentId): Observable<GradingPeriodCommentDTO[]> {
        let queryString = '';
        queryString += '?yearId=' + this.yearId +
            '&schoolId=' + this.schoolId +
            '&siteSectionId=' + this.course.Id +
            '&termId=' + termId +
            '&C360GradebookLimitNumberOfComments=' + C360GradebookLimitNumberOfComments +
            '&currentDate=' + currentDate +
            '&forAssignments=' + forAssignments +
            '&termCommentStudentId=' + termCommentStudentId;
        return this.http.get<GradingPeriodCommentDTO[]>(System.apiURL.Classroom + 'GetGradingPeriodComments' + queryString);
    }

    saveGradingPeriodComment(GradingPeriodCommentDTOs: GradingPeriodCommentDTO[]): Observable<GradingPeriodCommentDTO[]> {
        let queryString = '';
        queryString += '?courseId=' + this.course.Id;
        return this.http.post<GradingPeriodCommentDTO[]>(System.apiURL.Classroom + 'SaveGradingPeriodComment' + queryString, GradingPeriodCommentDTOs);
    }

    saveBehaviorSkillGrades(usingPercentageGrades: boolean, RCGBehaviorSkillsDTOs: BehaviorSkillGrades[]): Observable<boolean> {
        let queryString = '';
        queryString += '?siteYearId=' + this.siteYearId +
            '&courseId=' + this.course.Id +
            '&usingPercentageGrades=' + usingPercentageGrades +
            '&currentTermCycleId=' + this.gradebookSettings.CurrentGradeCycleId;
        return this.http.post<boolean>(System.apiURL.Classroom + 'SaveBehaviorSkillGrades' + queryString, RCGBehaviorSkillsDTOs);
    }

    saveCurriculumMarks(courseObjectives): Observable<boolean> {
        let queryString = '';
        queryString += '?courseId=' + this.course.Id;
        return this.http.post<boolean>(System.apiURL.Classroom + 'SaveCurriculumMarks' + queryString, courseObjectives);
    }

    getGradebookAssignmentLinkedByObjectives(selectedObjectiveId: number): Observable<any[]> {
        let queryString = '';
        queryString += '?courseId=' + this.course.Id +
            '&SubjectId=' + this.course.SubjectId +
            '&siteYearId=' + this.siteYearId +
            '&selectedStudentId=' + selectedObjectiveId +
            '&section=' + this.course.Section;
        return this.http.get<any[]>(System.apiURL.Classroom + 'GetGradebookAssignmentLinkedByObjectives' + queryString);
    }

    getGradebookAssignmentLinkedObjectivesByObjectiveId(selectedObjectiveId: number): Observable<any[]> {
        let queryString = '';
        queryString += '?courseId=' + this.course.Id +
            '&SubjectId=' + this.course.SubjectId +
            '&siteYearId=' + this.siteYearId +
            '&selectedObjectiveId=' + selectedObjectiveId +
            '&section=' + this.course.Section;
        return this.http.get<any[]>(System.apiURL.Classroom + 'GetGradebookAssignmentLinkedObjectivesByObjectiveId' + queryString);
    }

    getValidGradeTemplates(id: number, studentId: number): Observable<ValidGradeTemplate[]> {
        const subjectId = this.course != null && this.course.SubjectId != null ? this.course.SubjectId : 0;
        let queryString = '';
        queryString += '?siteYearId=' + this.siteYearId +
            '&id=' + id +
            '&studentId=' + studentId +
            '&subjectId=' + subjectId;
        return this.http.get<ValidGradeTemplate[]>(System.apiURL.Classroom + 'GetValidGradeTemplates' + queryString);
    }

    getValidGradeTemplatesBySiteYear(studentId: number, siteYearId: number): Observable<ValidGradeTemplate[]> {
        const subjectId = this.course != null && this.course.SubjectId != null ? this.course.SubjectId : 0;
        let queryString = '';
        queryString += '?siteYearId=' + siteYearId +
            '&id=0' +
            '&studentId=' + studentId +
            '&subjectId=' + subjectId;
        return this.http.get<ValidGradeTemplate[]>(System.apiURL.Classroom + 'GetValidGradeTemplates' + queryString);
    }

    finalizeGrades() {
        let queryString = '';
        queryString += '?courseId=' + this.course.Id +
            '&currentTermCycleId=' + this.gradebookSettings.CurrentGradeCycleId +
            '&finalize=' + true;
        return this.http.get(System.apiURL.Classroom + 'FinalizeGrades' + queryString);
    }

    importGradesFromExternalGradebook(attachment: TermGradesBackup[]): Observable<string> {
        let queryString = '';
        queryString += '?yearId=' + this.yearId +
            '&siteYearId=' + this.siteYearId +
            '&termGradeCycleId=' + this.gradebookSettings.CurrentGradeCycleId +
            '&currentTermId=' + 0 +
            '&TeacherCanGenerateEndofTermLetterGrade=' + this.gradebookSettings.TeacherCanImportObjectiveScores;
        return this.http.post<string>(System.apiURL.Classroom + 'ImportGradesFromExternalGradebook' + queryString, attachment);
    }

    // Indicators //
    getGradebookIndicators(courseId: number): Observable<GradebookIndicator[]> {
        return this.sharedGradeBookService.getGradebookIndicators(courseId, 0);
    }

    saveGradebookIndicator(gradebookIndicators: GradebookIndicator): Observable<boolean> {
        return this.sharedGradeBookService.saveGradebookIndicator(gradebookIndicators, this.course.Id);
    }

    deleteGradebookIndicator(id: number): Observable<boolean> {
        return this.sharedGradeBookService.deleteGradebookIndicator(id);
    }

    saveGradebookV9CopyIndicator(indicatorIds: string, siteSectionIds: string): Observable<string> {
        let queryString = '';
        queryString += '?indicatorIds=' + indicatorIds +
            '&siteSectionIds=' + siteSectionIds;
        return this.http.get<string>(System.apiURL.Classroom + 'SaveGradebookV9CopyIndicators' + queryString);
    }

    getGradebookV9AnalysisStudentsAttendance(): Observable<AnalysisStudentsAttendance[]> {
        let queryString = '';
        queryString += '?schoolId=' + this.schoolId +
            '&yearId=' + this.yearId +
            '&siteSectionId=' + this.course.Id;
        return this.http.get<AnalysisStudentsAttendance[]>(System.apiURL.Classroom + 'GetGradebookV9AnalysisStudents' + queryString);
    }

    exportBase64ToFile(base64Document: string, fileName: string, fileType: string) {
        const byteArrays: any[] = [];
        const byteCharacters = atob(base64Document);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
        const blob = new Blob(byteArrays, { type: fileType });

        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.style.visibility = 'hidden';
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
}
